import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function RegisterPage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(email, password, name);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex flex-1 relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #004d66 0%, #006080 50%, #008099 100%)" }}
      >
        <img
          src="https://images.unsplash.com/photo-1761888857629-aa3f306f6bee?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMHBhbGUlMjBnZW9tZXRyaWMlMjB0ZXh0dXJlfGVufDB8fHx8MTc3NjAwNzI3OXww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-overlay"
        />
        <div className="relative z-10 flex flex-col justify-center p-12 text-white">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-9 h-9 bg-white/20 rounded-lg flex items-center justify-center">
              <span className="text-white font-black text-lg">X</span>
            </div>
            <span className="font-bold text-xl tracking-tight">ExcelToAPI</span>
          </div>
          <h1 className="text-4xl font-black tracking-tight leading-tight mb-4">
            Get started in<br />minutes
          </h1>
          <p className="text-white/70 text-lg leading-relaxed max-w-sm">
            Create your account and start transforming your data workflows today.
          </p>
        </div>
      </div>

      <div className="flex-1 lg:max-w-md flex items-center justify-center p-8 bg-[#F8F9FA]">
        <div className="w-full max-w-sm fade-in">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Create account</h2>
            <p className="text-gray-500 text-sm mt-1">Fill in your details to get started</p>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="register-error">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-1.5">
              <Label htmlFor="name" className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Full Name</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jane Smith"
                required
                data-testid="register-name"
                className="bg-white border-gray-200 focus:border-[#006080]"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                data-testid="register-email"
                className="bg-white border-gray-200 focus:border-[#006080]"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 6 characters"
                required
                data-testid="register-password"
                className="bg-white border-gray-200 focus:border-[#006080]"
              />
            </div>
            <Button
              type="submit"
              className="w-full bg-[#006080] hover:bg-[#004d66] text-white font-semibold h-10"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? "Creating account..." : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-gray-500">
            Already have an account?{" "}
            <Link to="/login" className="text-[#006080] font-semibold hover:underline">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
